'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { HERO_MOTION } from './constants';
type Easing = (t: number) => number;
const easeOutCubic: Easing = t => 1 - (1 - t) ** 3;
const easeOutExpo: Easing = t => t >= 1 ? 1 : 1 - 2 ** (-10 * t);
interface CountUpOptions {
  durationMs?: number;
  ease?: Easing;
}

/**
 * Counts from the previously shown value to `target` once `play` is true.
 * Before playing it shows 0, so the number visibly grows when the hero enters the view.
 */
const useCountUp = (target, play, t0) => {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    durationMs: t2,
    ease: t3
  } = t1;
  const durationMs = t2 === undefined ? HERO_MOTION.countUp.durationMs : t2;
  const ease = t3 === undefined ? easeOutCubic : t3;
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  let t4;
  let t5;
  if ($[2] !== durationMs || $[3] !== ease || $[4] !== play || $[5] !== reduceMotion || $[6] !== target) {
    t4 = () => {
      if (!play || target === null || reduceMotion) {
        return;
      }
      const from = fromRef.current;
      const start = performance.now();
      let frame = 0;
      const tick = now => {
        const progress = Math.min((now - start) / durationMs, 1);
        const next = from + (target - from) * ease(progress);
        fromRef.current = next;
        setValue(next);
        if (progress < 1) {
          frame = requestAnimationFrame(tick);
        }
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    };
    t5 = [target, play, durationMs, ease, reduceMotion];
    $[2] = durationMs;
    $[3] = ease;
    $[4] = play;
    $[5] = reduceMotion;
    $[6] = target;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (target === null) {
    return null;
  }
  return reduceMotion && play ? target : value;
};
export default useCountUp;
export { easeOutCubic, easeOutExpo };